import { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Alert, AlertIcon, Badge, Box, Button, Collapse, FormControl, FormHelperText, FormLabel,
  Grid, GridItem, Heading, Input, Select, Text
} from '@chakra-ui/react'
import { UiSelect } from 'components/common'
import {
  getVerticalSupportOptions,
  lookupVerticalSupportEngineering,
  getEngineeringTubeSizes
} from 'api'
import {
  applyNoEmcFabricationMultiplier,
  calculateWallMountBracketsFromQuote,
  formatWallMountBracketSummary,
  preferWallMountPairOption
} from 'lib'

const INSTALLATION_TYPES = {
  DIRECT_BURIAL: 'direct_burial',
  SINGLE_ANCHOR: 'single_anchor',
  DUAL_ANCHOR: 'dual_anchor',
  WALL_MOUNT: 'wall_mount',
  RETROFIT: 'retrofit'
}

const INSTALLATION_TYPE_LABELS = {
  [INSTALLATION_TYPES.DIRECT_BURIAL]: 'Direct Burial - New Footer',
  [INSTALLATION_TYPES.SINGLE_ANCHOR]: 'Single Pole Anchor Bolt Foundation',
  [INSTALLATION_TYPES.DUAL_ANCHOR]: 'Dual Pole Anchor Bolt Foundation',
  [INSTALLATION_TYPES.WALL_MOUNT]: 'Wall Mount',
  [INSTALLATION_TYPES.RETROFIT]: 'Retro-Fit'
}

const PLATE_COSTS = {
  [INSTALLATION_TYPES.SINGLE_ANCHOR]: 250,
  [INSTALLATION_TYPES.DUAL_ANCHOR]: 350
}

const STUB_PLATE_COST = 350

const STUB_PLATE_OPTION = {
  id: '_stub_plate',
  description: 'Stub or Match Plate Mount',
  baseCost: 0,
  footCost: 0,
  _isStubPlate: true
}

function supportCountForInstallationType (installationType) {
  if (installationType === INSTALLATION_TYPES.WALL_MOUNT) return 0
  if (installationType === INSTALLATION_TYPES.DUAL_ANCHOR) return 2
  if (installationType) return 1
  return 0
}

function inchesToFtIn (totalInches) {
  if (!totalInches || totalInches <= 0) return ''
  const ft = Math.floor(totalInches / 12)
  const inches = totalInches % 12
  return `${ft}'-${inches}"`
}

function inchesToDecimalFt (totalInches) {
  if (!totalInches || totalInches <= 0) return 0
  return parseFloat((totalInches / 12).toFixed(2))
}

export const EdgeSupportForm = ({ form, setForm, ...rest }) => {
  const [tubeSizeOptions, setTubeSizeOptions] = useState([])
  const [lookupResult, setLookupResult] = useState(null)
  const [isOutOfRange, setIsOutOfRange] = useState(false)
  const [advisoryMessage, setAdvisoryMessage] = useState('')

  const oahTotalInches = useMemo(() => {
    const emcHeightIn = form.noEmc
      ? 0
      : (form.emcPricingVersion === 2
          ? (form.emcModsH || 0) * 12
          : (form.emcPricing?.modsH || 0) * 12)
    const cabinetHeightIn = form.noCabinet ? 0 : (form.cabinetHeightIn || 0)
    const pedestalHeightIn = form.noPedestal ? 0 : (form.pedestalHeightIn || 0)
    const additionalPoleHeightIn = form.noPedestal ? (form.pedestalAdditionalHeight || 0) : 0
    return emcHeightIn + cabinetHeightIn + pedestalHeightIn + additionalPoleHeightIn
  }, [
    form.noEmc, form.noCabinet, form.noPedestal,
    form.emcPricing?.modsH, form.emcModsH, form.emcPricingVersion,
    form.cabinetHeightIn, form.pedestalHeightIn, form.pedestalAdditionalHeight
  ])

  const oah = useMemo(() => {
    return parseFloat(`${parseInt(oahTotalInches / 12)}.${oahTotalInches % 12}`)
  }, [oahTotalInches])

  const oahFt = useMemo(() => {
    if (oahTotalInches <= 0) return 0
    return Math.ceil(oahTotalInches / 12)
  }, [oahTotalInches])

  const oawTotalInches = useMemo(() => {
    const widths = []
    if (!form.noEmc) widths.push((form.emcModsL || 0) * 12)
    if (!form.noCabinet) widths.push(form.cabinetLengthIn || 0)
    if (!form.noPedestal) widths.push(form.pedestalLengthIn || 0)
    return widths.length ? Math.max(...widths) : 0
  }, [form.noEmc, form.noCabinet, form.noPedestal, form.emcModsL, form.cabinetLengthIn, form.pedestalLengthIn])

  const oawFt = useMemo(() => {
    if (oawTotalInches <= 0) return 0
    return Math.ceil(oawTotalInches / 12)
  }, [oawTotalInches])

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getEngineeringTubeSizes()
        setTubeSizeOptions(res.allSizes || [])
      } catch (err) { /* ignore */ }
    }
    load()
  }, [])

  const isPoleBased = [INSTALLATION_TYPES.DIRECT_BURIAL, INSTALLATION_TYPES.SINGLE_ANCHOR, INSTALLATION_TYPES.DUAL_ANCHOR].includes(form.installationType)
  const isBracketBased = [INSTALLATION_TYPES.WALL_MOUNT, INSTALLATION_TYPES.RETROFIT].includes(form.installationType)
  const isWallMount = form.installationType === INSTALLATION_TYPES.WALL_MOUNT

  const wallMountCalc = useMemo(
    () => (isWallMount ? calculateWallMountBracketsFromQuote(form) : null),
    [
      isWallMount, form.noEmc, form.noCabinet, form.emcModsH, form.emcModsL,
      form.emcHeightFt, form.emcLengthFt, form.emcPricing?.modsH, form.emcPricing?.modsL,
      form.emcStackMode, form.cabinetHeightFt, form.cabinetHeightIn, form.cabinetLengthFt,
      form.cabinetLengthIn, form.cabinetStackMode, form.structureComponents
    ]
  )

  useEffect(() => {
    if (!isPoleBased || oahFt <= 0 || oawFt <= 0) {
      setLookupResult(null)
      setIsOutOfRange(false)
      setAdvisoryMessage('')
      return
    }

    if (oahFt < 5 || oahFt > 18) {
      setIsOutOfRange(true)
      setLookupResult(null)
      setAdvisoryMessage(oahFt < 5
        ? 'Sign height is below table coverage.'
        : 'Sign height exceeds table coverage. Engineering review required.')
      return
    }
    if (oawFt < 5 || oawFt > 10) {
      setIsOutOfRange(true)
      setLookupResult(null)
      setAdvisoryMessage(oawFt < 5
        ? 'Sign width is below table coverage.'
        : 'Sign width exceeds table coverage. Engineering review required.')
      return
    }

    const doLookup = async () => {
      try {
        const result = await lookupVerticalSupportEngineering(oahFt, oawFt)
        if (result.outOfRange) {
          setIsOutOfRange(true)
          setLookupResult(null)
          setAdvisoryMessage('Combination not found in engineering table.')
        } else {
          setIsOutOfRange(false)
          setLookupResult(result)
          setAdvisoryMessage(result.source === 'Extrapolated'
            ? 'Extrapolated data — verify with engineer for permitted use.'
            : '')
        }
      } catch (err) {
        setIsOutOfRange(true)
        setLookupResult(null)
      }
    }
    doLookup()
  }, [isPoleBased, oahFt, oawFt])

  const activeTubeSize = useMemo(() => {
    if (!isPoleBased) return ''
    if (form.isSupportOverride && form.overrideTubeSize) return form.overrideTubeSize
    if (isOutOfRange) return 'Structural Support – See Line Item'
    if (!lookupResult) return '—'

    if (form.installationType === INSTALLATION_TYPES.DUAL_ANCHOR) {
      return lookupResult.dualTubeSize
    }
    return lookupResult.tubeSize
  }, [isPoleBased, form.isSupportOverride, form.overrideTubeSize, form.installationType, isOutOfRange, lookupResult])

  const footerDepthIn = useMemo(() => {
    if (form.installationType !== INSTALLATION_TYPES.DIRECT_BURIAL) return 0
    if (isOutOfRange || !lookupResult) return 0
    return lookupResult.footerDepthIn
  }, [form.installationType, isOutOfRange, lookupResult])

  const steelTubeLengthIn = useMemo(() => {
    if (!isPoleBased || isOutOfRange) return 0

    if (form.installationType === INSTALLATION_TYPES.DIRECT_BURIAL) {
      if (!footerDepthIn) return 0
      return oahTotalInches + footerDepthIn - 6
    }
    return oahTotalInches
  }, [isPoleBased, isOutOfRange, form.installationType, oahTotalInches, footerDepthIn])

  const loadWallMountOptions = useCallback((page) => getVerticalSupportOptions(page, 50, '', 'id', 'asc', 'wall_mount'), [])
  const loadRetrofitBracketOptions = useCallback(async (page, search) => {
    const res = await getVerticalSupportOptions(page, 50, search || '', 'id', 'asc', 'retrofit')
    const rows = (res.rows || []).filter(
      r => r.id !== STUB_PLATE_OPTION.id && r.description !== STUB_PLATE_OPTION.description
    )
    if (page === 1) {
      return { ...res, rows: [STUB_PLATE_OPTION, ...rows] }
    }
    return { ...res, rows }
  }, [])

  useEffect(() => {
    if (form.installationType === INSTALLATION_TYPES.WALL_MOUNT) return
    const count = supportCountForInstallationType(form.installationType)
    if (count > 0 && form.numberOfSupports !== count) {
      setForm(draft => { draft.numberOfSupports = count })
    }
  }, [form.installationType])

  useEffect(() => {
    if (!isWallMount) return
    if (form.bracketOption) return
    let cancelled = false
    const load = async () => {
      try {
        const { rows } = await getVerticalSupportOptions(1, 50, '', 'id', 'asc', 'wall_mount')
        if (cancelled) return
        const match = preferWallMountPairOption(rows || [])
        if (!match) return
        setForm(draft => {
          if (draft.bracketOption) return
          draft.bracketOption = match
          draft.autoTubeSize = match.description || ''
        })
      } catch (err) { /* ignore */ }
    }
    load()
    return () => { cancelled = true }
  }, [isWallMount, form.bracketOption, setForm])

  useEffect(() => {
    if (!isPoleBased) {
      setForm(draft => {
        draft.autoTubeSize = ''
        draft.autoFooterDepthIn = 0
        draft.steelTubeLengthIn = 0
        draft.isSupportOverride = false
        draft.overrideTubeSize = ''
        draft.verticalSupportOption = null
        draft.verticalSupportCost = 0
        draft.plateCost = 0
      })
      return
    }

    setForm(draft => {
      draft.autoTubeSize = activeTubeSize
      draft.autoFooterDepthIn = footerDepthIn
      draft.steelTubeLengthIn = steelTubeLengthIn
    })
  }, [isPoleBased, activeTubeSize, footerDepthIn, steelTubeLengthIn])

  const applyPolePricing = useCallback((match) => {
    const tubeLengthDecimalFt = inchesToDecimalFt(steelTubeLengthIn)
    const supportCost = form.numberOfSupports * (match.baseCost + match.footCost * tubeLengthDecimalFt)
    const plateCostPerSupport = PLATE_COSTS[form.installationType] || 0
    const totalPlateCost = form.numberOfSupports * plateCostPerSupport
    setForm(draft => {
      draft.verticalSupportOption = match
      draft.verticalSupportCost = parseFloat(supportCost.toFixed(2))
      draft.plateCost = parseFloat(totalPlateCost.toFixed(2))
    })
  }, [steelTubeLengthIn, form.numberOfSupports, form.installationType, setForm])

  useEffect(() => {
    if (!isPoleBased) return

    const cannotPrice = isOutOfRange ||
      !activeTubeSize ||
      activeTubeSize === '—' ||
      activeTubeSize === 'Structural Support – See Line Item'

    if (cannotPrice) {
      setForm(draft => {
        draft.verticalSupportCost = 0
        draft.plateCost = 0
      })
      return
    }

    if (form.verticalSupportOption?.description === activeTubeSize) {
      applyPolePricing(form.verticalSupportOption)
      return
    }

    const loadPricing = async () => {
      try {
        const { rows } = await getVerticalSupportOptions(1, 50, activeTubeSize, 'id', 'asc', 'pole')
        const match = rows.find(r => r.description === activeTubeSize)
        if (match) applyPolePricing(match)
      } catch (err) { /* ignore */ }
    }
    loadPricing()
  }, [
    isPoleBased, isOutOfRange, activeTubeSize, steelTubeLengthIn, oahTotalInches,
    form.numberOfSupports, form.installationType, form.verticalSupportOption?.description,
    applyPolePricing
  ])

  useEffect(() => {
    if (!isBracketBased) return

    setForm(draft => {
      draft.autoTubeSize = ''
      draft.autoFooterDepthIn = 0
      draft.steelTubeLengthIn = 0
      draft.isSupportOverride = false
      draft.overrideTubeSize = ''
    })

    const qty = isWallMount
      ? (wallMountCalc?.pairQuantity || 0)
      : form.numberOfSupports

    if (isWallMount && form.numberOfSupports !== qty) {
      setForm(draft => { draft.numberOfSupports = qty })
    }

    if (form.bracketOption) {
      setForm(draft => {
        draft.autoTubeSize = form.bracketOption._isStubPlate
          ? STUB_PLATE_OPTION.description
          : (form.bracketOption.description || '')
      })

      if (form.bracketOption._isStubPlate) {
        const loadStubPricing = async () => {
          try {
            const { rows } = await getVerticalSupportOptions(1, 50, '4"x4"x3/16"', 'id', 'asc', 'pole')
            const match = rows.find(r => r.description === '4"x4"x3/16"')
            if (match) {
              const oahDecimalFt = inchesToDecimalFt(oahTotalInches)
              const supportCost = qty * (match.baseCost + match.footCost * oahDecimalFt)
              const totalPlateCost = applyNoEmcFabricationMultiplier(qty * STUB_PLATE_COST, form.noEmc)
              setForm(draft => {
                draft.verticalSupportCost = parseFloat(supportCost.toFixed(2))
                draft.plateCost = parseFloat(totalPlateCost.toFixed(2))
              })
            }
          } catch (err) { /* ignore */ }
        }
        loadStubPricing()
      } else {
        const cost = applyNoEmcFabricationMultiplier(qty * (form.bracketOption.baseCost || 0), form.noEmc)
        setForm(draft => {
          draft.verticalSupportCost = parseFloat(cost.toFixed(2))
          draft.plateCost = 0
        })
      }
    } else {
      setForm(draft => {
        draft.verticalSupportCost = 0
        draft.plateCost = 0
        draft.autoTubeSize = ''
      })
    }
  }, [isBracketBased, isWallMount, wallMountCalc?.pairQuantity, form.bracketOption?.id, form.bracketOption?._isStubPlate, form.numberOfSupports, oahTotalInches, form.noEmc])

  const handleBracketChange = (option) => {
    setForm(draft => {
      draft.bracketOption = option
      draft.autoTubeSize = option?._isStubPlate
        ? STUB_PLATE_OPTION.description
        : (option?.description || '')
    })
  }

  const handleInstallationTypeChange = (e) => {
    const newType = e.target.value
    setForm(draft => {
      draft.installationType = newType
      draft.numberOfSupports = supportCountForInstallationType(newType)
      draft.isSupportOverride = false
      draft.overrideTubeSize = ''
      draft.bracketOption = null
      draft.verticalSupportOption = null
      draft.verticalSupportCost = 0
      draft.plateCost = 0
      draft.footerStyleCost = 0
    })
  }

  const handleOverrideToggle = () => {
    setForm(draft => {
      draft.isSupportOverride = !draft.isSupportOverride
      if (!draft.isSupportOverride) {
        draft.overrideTubeSize = ''
      }
    })
  }

  const totalCostDisplay = (form.verticalSupportCost || 0) + (form.plateCost || 0)

  return (
    <Box {...rest}>
      <Grid
        templateColumns="5fr 1fr"
        rowGap={5}
        columnGap={10}
        w="900px"
        maxW="100%"
      >
        <GridItem>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Heading size="md">Supports & Brackets</Heading>
            <Box display="flex" gap={4} alignItems="center">
              <FormControl w="fit-content" display="flex" alignItems="center">
                <FormLabel mb={0} whiteSpace="nowrap">OAH</FormLabel>
                <Input maxW="80px" value={oah} readOnly size="sm" />
              </FormControl>
              <FormControl w="fit-content" display="flex" alignItems="center">
                <FormLabel mb={0} whiteSpace="nowrap">OAW</FormLabel>
                <Input maxW="80px" value={oawFt > 0 ? `${oawFt} ft` : '0'} readOnly size="sm" />
              </FormControl>
            </Box>
          </Box>
        </GridItem>
        <GridItem></GridItem>

        <GridItem>
          <FormControl isRequired>
            <FormLabel>Installation Type</FormLabel>
            <Select
              value={form.installationType}
              onChange={handleInstallationTypeChange}
              placeholder="Select Installation Type"
            >
              {Object.entries(INSTALLATION_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </Select>
          </FormControl>
        </GridItem>
        <GridItem></GridItem>

        {isPoleBased && (
          <>
            {advisoryMessage && (
              <GridItem colSpan={2}>
                <Alert status={isOutOfRange ? 'warning' : 'info'} borderRadius="md">
                  <AlertIcon />
                  {advisoryMessage}
                </Alert>
              </GridItem>
            )}

            <GridItem>
              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <FormControl>
                  <FormLabel>
                    {form.installationType === INSTALLATION_TYPES.DUAL_ANCHOR ? 'Dual Tube Size' : 'Tube Size'}
                    {form.isSupportOverride && (
                      <Badge ml={2} colorScheme="orange" fontSize="0.7em">Overridden</Badge>
                    )}
                  </FormLabel>
                  <Input value={activeTubeSize} readOnly bg={form.isSupportOverride ? 'orange.50' : undefined} />
                </FormControl>

                {form.installationType === INSTALLATION_TYPES.DIRECT_BURIAL && (
                <FormControl>
                  <FormLabel>Footer Depth</FormLabel>
                  <Input value={footerDepthIn ? inchesToFtIn(footerDepthIn) : (isOutOfRange ? '—' : '0')} readOnly />
                </FormControl>
                )}

                <FormControl>
                  <FormLabel>Steel Tube Length</FormLabel>
                  <Input
                    value={steelTubeLengthIn > 0
                      ? `${inchesToFtIn(steelTubeLengthIn)} (${inchesToDecimalFt(steelTubeLengthIn)} ft)`
                      : '0'}
                    readOnly
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Estimated Steel Cost</FormLabel>
                  <Input value={`$${(form.verticalSupportCost || 0).toFixed(2)}`} readOnly />
                </FormControl>

                {form.installationType in PLATE_COSTS && (
                  <FormControl>
                    <FormLabel>Plate Cost (${PLATE_COSTS[form.installationType]}/support)</FormLabel>
                    <Input value={`$${(form.plateCost || 0).toFixed(2)}`} readOnly />
                  </FormControl>
                )}
              </Grid>
            </GridItem>
            <GridItem display="flex" alignItems="flex-end">
              <FormControl>
                <FormLabel>Total</FormLabel>
                <Input value={`$${(totalCostDisplay || 0).toFixed(2)}`} readOnly />
              </FormControl>
            </GridItem>

            <GridItem>
              <Button
                variant="link"
                size="sm"
                colorScheme={form.isSupportOverride ? 'orange' : 'blue'}
                onClick={handleOverrideToggle}
              >
                {form.isSupportOverride ? 'Use recommended size' : 'Override recommendation'}
              </Button>
              <Collapse in={form.isSupportOverride} animateOpacity>
                <Box mt={2}>
                  <FormControl>
                    <FormLabel>Override Tube Size</FormLabel>
                    <Select
                      value={form.overrideTubeSize}
                      onChange={e => setForm(draft => { draft.overrideTubeSize = e.target.value })}
                      placeholder="Select tube size"
                    >
                      {tubeSizeOptions.map(size => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                      <option value="Structural Support – See Line Item">Structural Support – See Line Item</option>
                    </Select>
                  </FormControl>
                </Box>
              </Collapse>
            </GridItem>
            <GridItem></GridItem>
          </>
        )}

        {form.installationType === INSTALLATION_TYPES.WALL_MOUNT && (
          <>
            <GridItem>
              <FormControl isRequired>
                <FormLabel>Bracket / Hardware</FormLabel>
                <UiSelect
                  value={form.bracketOption}
                  onChange={handleBracketChange}
                  loadData={loadWallMountOptions}
                  apiRespProp="rows"
                  getOptionValue={v => v.id}
                  getOptionLabel={v => `${v.description} — $${v.baseCost}`}
                  placeholder="Select bracket"
                  noOptionsMessage={() => 'No options found'}
                  isSearchable={false}
                  required={true}
                />
              </FormControl>
            </GridItem>
            <GridItem></GridItem>
            <GridItem>
              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <FormControl>
                  <FormLabel>Qty (pairs)</FormLabel>
                  <Input value={wallMountCalc?.pairQuantity || 0} readOnly />
                  <FormHelperText>
                    {wallMountCalc?.pairQuantity
                      ? `${formatWallMountBracketSummary(wallMountCalc)} from ${wallMountCalc.widthFt} ft W × ${wallMountCalc.heightFt} ft H sign face.`
                      : 'Calculated from EMC and cabinet width and height. Enter those sizes to populate quantity.'}
                  </FormHelperText>
                </FormControl>
                <FormControl>
                  <FormLabel>Cost</FormLabel>
                  <Input value={form.verticalSupportCost} readOnly />
                  {form.bracketOption && (
                    <FormHelperText>
                      {form.noEmc
                        ? 'Includes No EMC fabrication markup'
                        : `${wallMountCalc?.pairQuantity || 0} × $${Number(form.bracketOption.baseCost || 0).toFixed(2)}/pair`}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </GridItem>
            <GridItem></GridItem>
          </>
        )}

        {form.installationType === INSTALLATION_TYPES.RETROFIT && (
          <>
            <GridItem>
              <FormControl isRequired>
                <FormLabel>Bracket / Hardware</FormLabel>
                <UiSelect
                  value={form.bracketOption}
                  onChange={handleBracketChange}
                  loadData={loadRetrofitBracketOptions}
                  apiRespProp="rows"
                  getOptionValue={v => v.id}
                  getOptionLabel={v => (
                    v._isStubPlate
                      ? 'Stub or Match Plate Mount (height + $350 plate)'
                      : `${v.description} — $${v.baseCost}`
                  )}
                  placeholder="Select bracket or stub plate"
                  noOptionsMessage={() => 'No options found'}
                  isSearchable={false}
                  required={true}
                />
              </FormControl>
            </GridItem>
            <GridItem display="flex" alignItems="flex-end">
              <FormControl>
                <FormLabel>Cost</FormLabel>
                <Input value={totalCostDisplay > 0 ? totalCostDisplay.toFixed(2) : form.verticalSupportCost} readOnly />
              </FormControl>
            </GridItem>
          </>
        )}

        {!form.installationType && (
          <GridItem colSpan={2}>
            <Text color="gray.400" fontSize="sm" fontStyle="italic">
              Select an installation type to configure supports and brackets.
            </Text>
          </GridItem>
        )}
      </Grid>
    </Box>
  )
}

EdgeSupportForm.propTypes = {
  form: PropTypes.object,
  setForm: PropTypes.func
}
